import {
  buildQuizScheduleTimestamp,
  formatQuizScheduleDateFromDb,
  formatQuizScheduleTimeFromDb,
} from './quiz-schedule-time';

export type SidebarQuizSettingsHandle = {
  hasUnsavedChanges: () => boolean;
  submitSettings: () => void;
};

export type QuizModeratorSettingsForm = {
  questions_from_date: string;
  questions_from_time: string;
  questions_to_date: string;
  questions_to_time: string;
  results_from_date: string;
  results_from_time: string;
  results_to_date: string;
  results_to_time: string;
  live: boolean;
  timer: number;
};

type PresentationSettingsRow = {
  live?: boolean | null;
  timer?: number | null;
  questions_from_time?: string | null;
  questions_to_time?: string | null;
  results_from_time?: string | null;
  results_to_time?: string | null;
};

export function buildSettingsFormFromPresentation(
  presentation: PresentationSettingsRow
): QuizModeratorSettingsForm {
  return {
    questions_from_date: formatQuizScheduleDateFromDb(presentation.questions_from_time),
    questions_from_time: formatQuizScheduleTimeFromDb(presentation.questions_from_time),
    questions_to_date: formatQuizScheduleDateFromDb(presentation.questions_to_time),
    questions_to_time: formatQuizScheduleTimeFromDb(presentation.questions_to_time),
    results_from_date: formatQuizScheduleDateFromDb(presentation.results_from_time),
    results_from_time: formatQuizScheduleTimeFromDb(presentation.results_from_time),
    results_to_date: formatQuizScheduleDateFromDb(presentation.results_to_time),
    results_to_time: formatQuizScheduleTimeFromDb(presentation.results_to_time),
    live: !!presentation.live,
    timer: presentation.timer ?? 30,
  };
}

function normalizeTimer(value: number | string | null | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function scheduleFieldEqual(
  currentDate: string,
  currentTime: string,
  baselineDate: string,
  baselineTime: string
): boolean {
  const currentTs = buildQuizScheduleTimestamp(currentDate, currentTime)?.toISO();
  const baselineTs = buildQuizScheduleTimestamp(baselineDate, baselineTime)?.toISO();

  if (!currentTs && !baselineTs) {
    return currentDate === baselineDate && currentTime === baselineTime;
  }

  return currentTs === baselineTs;
}

export function hasQuizModeratorSettingsChanges(
  current: QuizModeratorSettingsForm,
  baseline: QuizModeratorSettingsForm
): boolean {
  if (!!current.live !== !!baseline.live) {
    return true;
  }

  if (normalizeTimer(current.timer) !== normalizeTimer(baseline.timer)) {
    return true;
  }

  const schedulePairs: Array<[string, string, string, string]> = [
    [
      current.questions_from_date,
      current.questions_from_time,
      baseline.questions_from_date,
      baseline.questions_from_time,
    ],
    [
      current.questions_to_date,
      current.questions_to_time,
      baseline.questions_to_date,
      baseline.questions_to_time,
    ],
    [
      current.results_from_date,
      current.results_from_time,
      baseline.results_from_date,
      baseline.results_from_time,
    ],
    [
      current.results_to_date,
      current.results_to_time,
      baseline.results_to_date,
      baseline.results_to_time,
    ],
  ];

  return schedulePairs.some(
    ([currentDate, currentTime, baselineDate, baselineTime]) =>
      !scheduleFieldEqual(currentDate, currentTime, baselineDate, baselineTime)
  );
}
